/**
 * VenueProfilePanel — always-visible right panel showing the selected venue's public profile.
 * Fetches by venue_name via trpc.venue.getByVenueName.
 */

import { useState } from 'react';
import { trpc } from '../utils/trpc';
import { supabase } from '../utils/supabase';
import styles from '../styles/Venueprofilepanel.module.css';
import { buildVenueMapQuery } from '../utils/location';
import VenueMapEmbed from './VenueMapEmbed';

interface Props {
  venueName: string;
  onClose?: () => void;
}

function VenueProfilePanel({ venueName }: Props) {
  const [showTags, setShowTags] = useState(false);

  const {
    data: venue,
    isLoading: venueLoading,
    error: venueError,
  } = trpc.venue.getByVenueName.useQuery({ venueName }, { enabled: !!venueName });

  const { data: venueTags } = trpc.tags.getVenueTags.useQuery(
    { venueId: venue?.venue_id || '' },
    { enabled: !!venue?.venue_id },
  );

  const { data: galleryImages } = trpc.venue.getImages.useQuery(
    { venue_id: venue?.venue_id || '' },
    { enabled: !!venue?.venue_id },
  );

  const formatTime = (time: string | null) => {
    if (!time) return null;
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const tags = venueTags?.map((vt: any) => vt.tags.name) || [];
  const openTime = formatTime(venue?.open_time ?? null);
  const closeTime = formatTime(venue?.close_time ?? null);
  const mapQuery = buildVenueMapQuery(venue?.location, venue?.address);

  return (
    <aside className={styles.panel}>
      <div className={styles.panelHeader}>
        <span className={styles.panelTitle}>Venue Profile</span>
      </div>

      <div className={styles.panelBody}>
        {venueLoading && (
          <div className={styles.skeletonWrap}>
            <div className={styles.skeletonBanner} />
            <div className={styles.skeletonTitle} />
            <div className={styles.skeletonLine} />
            <div className={styles.skeletonLine} style={{ width: '60%' }} />
          </div>
        )}

        {venueError && !venueLoading && (
          <p className={styles.errorText}>Could not load venue: {venueError.message}</p>
        )}

        {venue && !venueLoading && (
          <>
            {venue.banner_url ? (
              <div className={styles.bannerWrap}>
                <img
                  onLoad={(e) => e.currentTarget.classList.add('loaded')}
                  src={venue.banner_url}
                  alt="Venue banner"
                  className={styles.bannerImg}
                />
              </div>
            ) : (
              <div className={styles.bannerPlaceholder} />
            )}

            <div className={styles.cardInner}>
              <h2 className={styles.venueName}>{venue.venue_name}</h2>

              {venue.desc && (
                <div className={styles.infoField}>
                  <span className={styles.label}>Description</span>
                  <span className={styles.value}>{venue.desc}</span>
                </div>
              )}

              {openTime && closeTime && (
                <div className={styles.infoField}>
                  <span className={styles.label}>Hours</span>
                  <span className={styles.value}>
                    {openTime} – {closeTime}
                  </span>
                </div>
              )}

              {(venue.address || venue.max_capacity) && (
                <div className={styles.infoRow}>
                  {venue.address && (
                    <div className={styles.infoField}>
                      <span className={styles.label}>Location</span>
                      <span className={styles.value}>{venue.address}</span>
                    </div>
                  )}
                  {venue.max_capacity && (
                    <div className={styles.infoField}>
                      <span className={styles.label}>Capacity</span>
                      <span className={styles.value}>{venue.max_capacity}</span>
                    </div>
                  )}
                </div>
              )}

              {mapQuery && (
                <div className={styles.mapSection}>
                  <span className={styles.sectionTitle}>Map</span>
                  <VenueMapEmbed
                    title={`${venue.venue_name} location`}
                    query={mapQuery}
                    className={styles.mapEmbed}
                  />
                </div>
              )}

              <div className={styles.tagsSection}>
                <div className={styles.tagsHeader}>
                  <span className={styles.label}>Interests</span>
                  <button
                    className={styles.tagsToggle}
                    onClick={() => setShowTags((p) => !p)}
                    aria-expanded={showTags}
                  >
                    {showTags ? 'Hide' : 'View'}
                    <span
                      className={`${styles.tagsChevron} ${showTags ? styles.tagsChevronOpen : ''}`}
                    >
                      ▼
                    </span>
                  </button>
                </div>
                <div
                  className={`${styles.tagsBody} ${showTags ? styles.tagsBodyOpen : ''}`}
                >
                  {tags.length > 0 ? (
                    <div className={styles.tagsContainer}>
                      {tags.map((tag: string, i: number) => (
                        <span key={i} className={styles.tag}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.emptyTags}>
                      <p className={styles.emptyTagsText}>No interests yet</p>
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.gallerySection}>
                <span className={styles.sectionTitle}>Gallery</span>
                {galleryImages && galleryImages.length > 0 ? (
                  <div className={styles.gallery}>
                    {galleryImages.map((img: any) => (
                      <div key={img.id} className={styles.galleryItem}>
                        <img
                          onLoad={(e) => e.currentTarget.classList.add('loaded')}
                          src={
                            supabase.storage.from('venues').getPublicUrl(img.storage_path)
                              .data.publicUrl
                          }
                          alt="Venue"
                          className={styles.galleryImage}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={styles.emptyGallery}>
                    <p className={styles.emptyText}>No photos yet</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </aside>
  );
}

export default VenueProfilePanel;
